import { api } from "@stoneflow/backend/convex/_generated/api"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useAction } from "convex/react"
import { type FormEvent, useState } from "react"
import { Button } from "#/components/ui/button"
import { Input } from "#/components/ui/input"

export const Route = createFileRoute("/demo/")({
	component: DemoLandingPage,
})

function DemoLandingPage() {
	const navigate = useNavigate()
	const provisionDemo = useAction(api.demo.provision.create)
	const [email, setEmail] = useState("")
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	async function handleSubmit(e: FormEvent) {
		e.preventDefault()
		setError(null)
		setLoading(true)

		try {
			const result = await provisionDemo({ email })
			navigate({
				to: "/demo/$uuid",
				params: { uuid: result.uuid },
				search: { ticket: result.ticket, orgId: result.orgId },
			})
		} catch (err) {
			const message = err instanceof Error ? err.message : ""
			if (message.includes("capacity")) {
				setError("We're at capacity right now. Please try again in a few minutes.")
			} else if (message.includes("already associated")) {
				setError(
					"This email is already associated with an account. Please use a different email, or sign in.",
				)
			} else {
				setError("Something went wrong. Please try again.")
			}
		} finally {
			setLoading(false)
		}
	}

	return (
		<main className="flex min-h-screen items-center justify-center bg-background px-4">
			<div className="w-full max-w-md rounded-xl border border-border bg-card p-8">
				<h1 className="mb-2 text-center text-3xl font-semibold tracking-tight text-foreground">
					Stoneflow
				</h1>
				<h2 className="mb-2 text-center text-lg font-medium text-foreground">
					Try Stoneflow — no sign-up required
				</h2>
				<p className="mb-6 text-center text-sm text-foreground-secondary">
					Enter your email to get instant access to a fully functional demo environment. Your demo
					expires after 24 hours.
				</p>

				<form onSubmit={handleSubmit} className="space-y-4">
					<Input
						type="email"
						required
						placeholder="you@example.com"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						disabled={loading}
					/>

					{error && <p className="text-sm text-severe">{error}</p>}

					<Button type="submit" className="w-full" disabled={loading}>
						{loading ? "Setting up your demo..." : "Start Demo"}
					</Button>
				</form>

				<p className="mt-6 text-center text-sm text-foreground-secondary">
					Already have an account?{" "}
					<a href="/sign-in" className="text-foreground underline underline-offset-4">
						Sign in
					</a>
				</p>
			</div>
		</main>
	)
}
